import produce from 'immer';

import {
  actionTypes as dateRangePickerActionTypes,
  reducerValuesFunction as dateRangePickerReducerFunction
} from '@components/Kendo/DateRangePickerReducer';

export const actionTypes = {
  PAGE_CHANGED: 'PAGE_CHANGED',
  FILTER_CHANGED: 'FILTER_CHANGED',
  SORT_CHANGED: 'SORT_CHANGED',
  COLUMNS_CHANGED: 'COLUMNS_CHANGED',
  DATA_LOADED: 'DATA_LOADED',
  LOADING_CHANGED: 'LOADING_CHANGED',
  FILTER_TOGGLED: 'FILTER_TOGGLED',
  ALL_SELECTED: 'ALL_SELECTED',
  SELECTION_CHANGED: 'SELECTION_CHANGED',
  HEADER_SELECTION_CHANGED: 'HEADER_SELECTION_CHANGED',
  SEARCH_CHANGED: 'SEARCH_CHANGED',
  ROW_STATUS_UPDATED: 'ROW_STATUS_UPDATED',
  ...dateRangePickerActionTypes
};

export const reducer = produce((draft, action) => {
  if (Object.keys(dateRangePickerActionTypes).includes(action.type)) {
    dateRangePickerReducerFunction(draft, action);
    draft.selectAll = false;
    draft.page.skip = null;
    draft.page.skip = 0;
    return;
  }

  switch (action.type) {
    case actionTypes.PAGE_CHANGED:
      draft.selectAll = false;
      draft.page = action.page;
      break;
    case actionTypes.FILTER_CHANGED:
      if (action.filters) {
        draft.filters = action.filters;
      } else {
        draft.filters.values[action.field] = action.value;
      }
      draft.selectAll = false;
      draft.page.skip = 0;
      break;
    case actionTypes.LOADING_CHANGED:
      draft.loading = action.loading;
      break;
    case actionTypes.FILTER_TOGGLED:
      draft.filters.active[action.field] = !draft.filters.active[action.field];
      draft.filters.values[action.field] = '';
      draft.selectAll = false;
      draft.page.skip = 0;
      break;
    case actionTypes.SORT_CHANGED:
      draft.sort = action.sort;
      draft.selectAll = false;
      draft.page.skip = 0;
      break;
    case actionTypes.COLUMNS_CHANGED:
      draft.columns = action.columns;
      break;
    case actionTypes.DATA_LOADED:
      draft.data = action.data.data;
      draft.totalCount = action.data.total_count;
      draft.loading = false;
      break;
    case actionTypes.ALL_SELECTED:
      draft.selectAll = true;
      draft.data.forEach((item) => {
        item.selected = true;
      });
      break;
    case actionTypes.SELECTION_CHANGED: {
      draft.selectAll = false;
      const item = draft.data.find((itemElem) => itemElem.id === action.id);
      item.selected = !item.selected;
      break;
    }
    case actionTypes.HEADER_SELECTION_CHANGED:
      if (!action.checked) draft.selectAll = false;
      draft.data.forEach((item) => {
        item.selected = action.checked;
      });
      break;
    case actionTypes.SEARCH_CHANGED:
      draft.search = action.search;
      draft.selectAll = false;
      draft.page.skip = 0;
      break;
    // Repaint the one row rather than refetching the page: a status change does not
    // move the row, and a refetch would throw away the user's scroll position.
    // The payload comes back from the server, so the row shows what was persisted
    // rather than what was clicked.
    case actionTypes.ROW_STATUS_UPDATED: {
      const row = draft.data.find((item) => item.id === action.proposal.id);
      if (row) {
        row.statusId = action.proposal.statusId;
        row.status = action.proposal.status;
        row.statusIcon = action.proposal.statusIcon;
        row.statusColor = action.proposal.statusColor;
        // IP-15601: the status drives this one. Accepting sets the approved date,
        // reverting to draft clears it back to N/A, so it has to be repainted with
        // the status or the cell keeps its stale value until a full reload.
        row.approvedDate = action.proposal.approvedDate;
      }

      // ...unless status is what the grid is filtered or sorted by. Then the repaint
      // alone leaves the page lying: a row filtered to "Draft" stays visible after
      // being set to "Accepted", and a status-sorted grid keeps it at its old
      // position. In that case refetch so the server reapplies both.
      const filteredByStatus = draft.filters.active.status && Boolean(draft.filters.values.status);
      const sortedByStatus = (draft.sort || []).some((entry) => entry.field === 'status');

      if (filteredByStatus || sortedByStatus) {
        // Same idiom the other datatables use to force the fetch effect to refire when
        // skip is already 0 (see the deficiencies index reducer).
        draft.selectAll = false;
        draft.page.skip = null;
        draft.page.skip = 0;
      }
      break;
    }
    default:
      throw new Error('Reducer - Wrong action type.');
  }
});
